import React, { useState, useEffect } from 'react'
import {
  ArrowLeft,
  Save,
  RotateCcw
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import assetMaintenanceService from '../../services/assetMaintenanceService'
import assetAcquisitionService from '../../services/assetAcquisitionService'
import assetDropdownService, { ASSET_DROPDOWN_FALLBACKS, optionsFromDropdown } from '../../services/assetDropdownService'
import { toDateInputValue } from './assetUtils'
import { labelStyle, inputStyle, textareaStyle, gridStyle, sectionStyle, fullWidthStyle, requiredStyle } from './assetFormStyles'

const emptyForm = {
  maintenanceCode: '',
  acquisition: '',
  provider: '',
  maintenanceType: 'Preventive',
  scheduledDate: '',
  completedDate: '',
  cost: 0,
  currency: 'INR',
  status: 'Scheduled',
  description: '',
  notes: ''
}

const AssetMaintenanceForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const maintenanceData = location.state?.maintenanceData
  const recordId = searchParams.get('id') || maintenanceData?._id

  const [formData, setFormData] = useState(emptyForm)
  const [acquisitions, setAcquisitions] = useState([])
  const [providers, setProviders] = useState([])
  const [typeOptions, setTypeOptions] = useState(optionsFromDropdown(null, 'maintenanceType', ASSET_DROPDOWN_FALLBACKS.maintenanceType))
  const [statusOptions, setStatusOptions] = useState(optionsFromDropdown(null, 'maintenanceStatus', ASSET_DROPDOWN_FALLBACKS.maintenanceStatus))
  const [currencyOptions, setCurrencyOptions] = useState(optionsFromDropdown(null, 'currency', ASSET_DROPDOWN_FALLBACKS.currency))
  const [isLoadingCode, setIsLoadingCode] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  // Fetch next maintenance code (only for new records)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchCode = async () => {
        try {
          setIsLoadingCode(true)
          const response = await assetMaintenanceService.getNextMaintenanceCode()
          if (response.success && response.data) {
            setFormData(prev => ({ ...prev, maintenanceCode: response.data.maintenanceCode || 'AMNT0001' }))
          }
        } catch (error) {
          console.error('Error fetching maintenance code:', error)
          setFormData(prev => ({ ...prev, maintenanceCode: 'AMNT0001' }))
        } finally {
          setIsLoadingCode(false)
        }
      }
      fetchCode()
    } else {
      setIsLoadingCode(false)
    }
  }, [isEditMode, isViewMode])

  useEffect(() => {
    loadDropdowns()
  }, [])

  useEffect(() => {
    if ((isEditMode || isViewMode) && recordId) {
      loadMaintenanceData(recordId)
    }
  }, [isEditMode, isViewMode, recordId])

  const loadDropdowns = async () => {
    try {
      const [acquisitionsResponse, dropdownResponse] = await Promise.all([
        assetAcquisitionService.getAllAcquisitions({ limit: 1000 }),
        assetDropdownService.getOptions()
      ])

      if (acquisitionsResponse.success) setAcquisitions(acquisitionsResponse.data || [])
      if (dropdownResponse.success && dropdownResponse.data) {
        setProviders(dropdownResponse.data.providers || [])
        setTypeOptions(optionsFromDropdown(dropdownResponse.data.dropdowns, 'maintenanceType', ASSET_DROPDOWN_FALLBACKS.maintenanceType))
        setStatusOptions(optionsFromDropdown(dropdownResponse.data.dropdowns, 'maintenanceStatus', ASSET_DROPDOWN_FALLBACKS.maintenanceStatus))
        setCurrencyOptions(optionsFromDropdown(dropdownResponse.data.dropdowns, 'currency', ASSET_DROPDOWN_FALLBACKS.currency))
      }
    } catch (error) {
      console.error('Error loading maintenance dropdown data:', error)
      if (!error._errorHandled) toast.error('Failed to load dropdown options')
    }
  }

  const loadMaintenanceData = async (id) => {
    try {
      setIsLoading(true)
      const response = await assetMaintenanceService.getMaintenanceById(id)
      if (response.success && response.data) {
        const record = response.data
        setFormData({
          maintenanceCode: record.maintenanceCode || '',
          acquisition: record.acquisition?._id || record.acquisition || '',
          provider: record.provider?._id || record.provider || '',
          maintenanceType: record.maintenanceType || 'Preventive',
          scheduledDate: toDateInputValue(record.scheduledDate),
          completedDate: toDateInputValue(record.completedDate),
          cost: record.cost || 0,
          currency: record.currency || 'INR',
          status: record.status || 'Scheduled',
          description: record.description || '',
          notes: record.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading maintenance data:', error)
      toast.error('Failed to load maintenance data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }))
  }

  const handleSave = async () => {
    try {
      if (!formData.acquisition) {
        toast.error('Please select the asset being maintained')
        return
      }

      setIsLoading(true)

      const submitData = {
        ...formData,
        acquisition: formData.acquisition || null,
        provider: formData.provider || null,
        scheduledDate: formData.scheduledDate || null,
        completedDate: formData.completedDate || null
      }

      if (isEditMode && recordId) {
        const response = await assetMaintenanceService.updateMaintenance(recordId, submitData)
        if (response.success) {
          toast.success('Maintenance record updated successfully!')
          navigate('/assets/maintenance')
        } else {
          toast.error(response.message || 'Failed to update maintenance record')
        }
      } else {
        const response = await assetMaintenanceService.createMaintenance(submitData)
        if (response.success) {
          toast.success('Maintenance record created successfully!')
          navigate('/assets/maintenance')
        } else {
          toast.error(response.message || 'Failed to create maintenance record')
        }
      }
    } catch (error) {
      console.error('Error saving maintenance record:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else if (!error._errorHandled) {
        toast.error('Failed to save maintenance record')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if ((isEditMode || isViewMode) && recordId) {
      loadMaintenanceData(recordId)
    } else {
      setFormData({ ...emptyForm, maintenanceCode: formData.maintenanceCode })
    }
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Assets', path: '/assets' },
          { label: 'Maintenance', path: '/assets/maintenance' },
          { label: isEditMode ? 'Edit' : isViewMode ? 'View' : 'New', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Maintenance' : isViewMode ? 'View Maintenance' : 'New Maintenance'}</h1>
          <p>{isViewMode ? 'View maintenance details' : isEditMode ? 'Update maintenance information' : 'Schedule asset maintenance'}</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/assets/maintenance')}>
            <ArrowLeft size={16} />
            Back
          </button>
          {!isViewMode && (
            <>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleReset}>
                <RotateCcw size={16} />
                Reset
              </button>
              <button type="button" className="btn-primary" onClick={handleSave} disabled={isLoading}>
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Maintenance Entry</h2>
        </div>
        <form className="form-content">
          <div className="section-header">
            <h3>Maintenance Details</h3>
          </div>
          <div style={sectionStyle}>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>
                  Maintenance Code <span style={requiredStyle}>*</span>
                </label>
                <input
                  type="text"
                  name="maintenanceCode"
                  value={formData.maintenanceCode}
                  onChange={handleInputChange}
                  disabled={isViewMode || isEditMode || isLoadingCode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  Asset <span style={requiredStyle}>*</span>
                </label>
                <select
                  name="acquisition"
                  value={formData.acquisition}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  <option value="">Select Asset</option>
                  {acquisitions.map(item => (
                    <option key={item._id} value={item._id}>
                      {item.assetName} ({item.acquisitionCode})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Provider</label>
                <select
                  name="provider"
                  value={formData.provider}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  <option value="">Select Provider</option>
                  {providers.map(provider => (
                    <option key={provider._id} value={provider._id}>
                      {provider.providerName} ({provider.providerCode})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Maintenance Type</label>
                <select
                  name="maintenanceType"
                  value={formData.maintenanceType}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  {typeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div style={fullWidthStyle}>
                <label style={labelStyle}>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  rows={3}
                  style={textareaStyle}
                />
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Schedule &amp; Cost</h3>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={gridStyle}>
              <div>
                <label style={labelStyle}>Scheduled Date</label>
                <input
                  type="date"
                  name="scheduledDate"
                  value={formData.scheduledDate}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Completed Date</label>
                <input
                  type="date"
                  name="completedDate"
                  value={formData.completedDate}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Cost</label>
                <input
                  type="number"
                  name="cost"
                  value={formData.cost}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  min="0"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Currency</label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  {currencyOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={inputStyle}
                >
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Additional Information</h3>
            </div>
          </div>
          <div style={sectionStyle}>
            <div style={fullWidthStyle}>
              <label style={labelStyle}>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                disabled={isViewMode}
                rows={4}
                style={textareaStyle}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AssetMaintenanceForm
